/** @format */

import React, { FC, useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { Drawer, ButtonGroup, Button, Table, Icon, Tooltip } from 'kpc-react'
import { useAsync } from 'react-use'
import { CloseFooter } from 'src/components'
import {
  AnalysisConfig,
  edgeTabelData,
  alarmColor,
  handleLinkColor,
  MetricObject,
  handleValueUnit
} from './constants'
import Api from 'src/api/ApiCloud'
import { request } from 'src/utils'

interface EdgeDrawerProps {
  className?: string
  visible: boolean
  onClose: () => void
  dataSource: string
  edgeData: any
  analysisSwitch: boolean
  analysisConfig: AnalysisConfig[]
  metricList: MetricObject[]
  nodes: any[]
}

const EdgeDrawer: FC<EdgeDrawerProps> = ({
  className,
  visible,
  onClose,
  dataSource,
  edgeData,
  analysisSwitch,
  analysisConfig,
  metricList,
  nodes
}) => {
  const [active, setActive] = useState<string>('')
  const [data, setData] = useState<any>()

  // 获取端口信息
  const { value: port_value, loading: port_loading } = useAsync(async () => {
    if (visible && edgeData) {
      const { source, target } = edgeData
      const source_result = await request(Api.getUFMPort, {
        data: { DataSource: dataSource, System: source }
      })
      const target_result = await request(Api.getUFMPort, {
        data: { DataSource: dataSource, System: target }
      })
      const source_data = source_result?.data?.ListPortsResult?.Data || []
      const target_data = target_result?.data?.ListPortsResult?.Data || []
      return {
        source: [...source_data.map(item => ({ ...item, guid: source }))],
        target: [...target_data.map(item => ({ ...item, guid: target }))]
      }
    } else {
      return {}
    }
  }, [visible, edgeData])

  // 抽屉打开默认值
  useEffect(() => {
    if (visible && edgeData) {
      setActive(edgeData?.links[0].Name)
      setData(edgeData?.links[0])
    }
  }, [visible, edgeData])

  // 切换LINK按钮
  const onChangeLink = value => {
    if (edgeData?.links?.length) {
      setActive(value)
      const findLink = edgeData?.links?.find(({ Name }) => Name === value)
      findLink && setData(findLink)
    }
  }
  // 处理数据
  const portTableData = useMemo(() => {
    if (data && nodes?.length) {
      const {
        SourceGUID,
        SourcePortDname,
        DestinationGUID,
        DestinationPortDname,
        MonitoringCounters
      } = data
      const { SourcePortCounters, DestinationPortCounters } = MonitoringCounters
      const tabelData = edgeTabelData?.map((column, index) => {
        switch (index) {
          case 0: //系统GUID
            column.source = SourceGUID || '-'
            column.desination = DestinationGUID || '-'
            break
          case 1: //设备名称
            const source_find = nodes.find(({ GUID }) => GUID === SourceGUID)
            const desination_find = nodes.find(({ GUID }) => GUID === DestinationGUID)
            column.source = source_find?.SystemName || '-'
            column.desination = desination_find?.SystemName || '-'
            break
          case 2: // 端口
            column.source = SourcePortDname || '-'
            column.desination = DestinationPortDname || '-'
            break
          case 3: // MTU（端口）
            column.source = port_value?.source?.length ? port_value?.source?.[0]?.Mtu : '-'
            column.desination = port_value?.target?.length ? port_value?.target?.[0].Mtu : '-'
            break
          case 4: // 带宽（端口）
            column.source = port_value?.source?.length ? port_value?.source?.[0]?.ActiveWidth : '-'
            column.desination = port_value?.target?.length
              ? port_value?.target?.[0].ActiveWidth
              : '-'
            break
          case 5: // 速度（端口）
            column.source = port_value?.source?.length ? port_value?.source?.[0]?.ActiveSpeed : '-'
            column.desination = port_value?.target?.length
              ? port_value?.target?.[0].ActiveSpeed
              : '-'
            break
          case 6:
            column.source = SourcePortCounters['Port_RX_Data']
            column.desination = DestinationPortCounters['Port_RX_Data']
            break
          case 7:
            column.source = SourcePortCounters['Port_TX_Data']
            column.desination = DestinationPortCounters['Port_TX_Data']
            break
          case 8:
            column.source = SourcePortCounters['Port_RX_Data_Rate']
            column.desination = DestinationPortCounters['Port_RX_Data_Rate']
            break
          case 9:
            column.source = SourcePortCounters['Port_TX_Data_Rate']
            column.desination = DestinationPortCounters['Port_TX_Data_Rate']
            break
          case 10:
            column.source = SourcePortCounters['Port_RX_Packets']
            column.desination = DestinationPortCounters['Port_RX_Packets']
            break
          case 11:
            column.source = SourcePortCounters['Port_RX_Packet_Rate']
            column.desination = DestinationPortCounters['Port_RX_Packet_Rate']
            break
          case 12:
            column.source = SourcePortCounters['Port_TX_Packets']
            column.desination = DestinationPortCounters['Port_TX_Packets']
            break
          case 13:
            column.source = SourcePortCounters['Port_TX_Packet_Rate']
            column.desination = DestinationPortCounters['Port_TX_Packet_Rate']
            break
          // case 14:
          //   column.source = SourcePortCounters['Normalized_Congestion_BW']
          //   column.desination = DestinationPortCounters['Normalized_Congestion_BW']
          //   break
          // case 14:
          //   column.source = SourcePortCounters['Normalized_TX_Data']
          //   column.desination = DestinationPortCounters['Normalized_TX_Data']
          //   break
        }
        return column
      })
      return tabelData
    } else {
      return edgeTabelData
    }
  }, [data, port_value])

  // 链路分析染色
  const handleTableColor = (name, value) => {
    if (metricList?.length) {
      const unit =
        metricList.find(({ Metric }) => {
          return Metric.replace(/_/g, ' ') === name
        })?.Units || ''
      if (analysisSwitch && analysisConfig?.length) {
        const findConfig = analysisConfig.find(({ Metric }) => Metric.replace(/_/g, ' ') === name)
        if (findConfig) {
          const { Compare, Normal, Warn, Error, Serious } = findConfig
          const level = [Serious, Error, Warn, Normal].findIndex(item => {
            if (item) {
              if (Compare === '>') {
                return value > parseFloat(item)
              } else if (Compare === '<') {
                return value < parseFloat(item)
              } else {
                return value === parseFloat(item)
              }
            } else {
              return false
            }
          })
          let color = ''
          if (level >= 0) {
            const colors = Object.values(alarmColor).reverse()
            color = colors[level] || ''
          }
          return <span style={{ color }}>{handleValueUnit(value, unit)}</span>
        } else {
          return <span>{handleValueUnit(value, unit)}</span>
        }
      } else {
        return <span>{handleValueUnit(value, unit)}</span>
      }
    } else {
      return '-'
    }
  }

  // Link Button染色
  const handeLinkColor = link => {
    if (analysisSwitch && analysisConfig?.length) {
      const color = handleLinkColor([link], analysisConfig)
      return active !== link.Name ? { color } : {}
    }
    return {}
  }

  const scheme = {
    name: {
      title: '名称',
      width: 150,
      template: ({ name }) => {
        const find = metricList?.find(item => item.Metric === name.replace(/ /g, '_'))
        return (
          <Tooltip content={find?.MetricDesc || ''}>
            <span>{name}</span>
          </Tooltip>
        )
      }
    },
    source: {
      title: '源信息',
      template: ({ name, source }) => handleTableColor(name, source)
    },
    desination: {
      title: '目标信息',
      template: ({ name, desination }) => handleTableColor(name, desination)
    }
  }

  return (
    <div id="edge_drawer">
      <Drawer
        value={visible}
        width={600}
        container={() => document.getElementById('edge_drawer')}
        on$change-value={(c, show) => !show && onClose()}
        title="链路详情"
        b-footer={<CloseFooter onClose={onClose} />}
      >
        <div className={className}>
          <ButtonGroup
            checkType="radio"
            value={active}
            on$change-value={(c, value) => {
              onChangeLink(value)
            }}
          >
            {edgeData?.links?.map((link, index) => (
              <Button style={handeLinkColor(link)} key={link.Name} value={link.Name}>
                {`LINK${index + 1}`}
              </Button>
            ))}
          </ButtonGroup>
          <p className="title">端口信息</p>
          <Table
            loading={port_loading}
            data={portTableData || []}
            checkType="none"
            scheme={scheme}
          ></Table>
          {/* <p className="title">线路信息</p>
          <Row1 gutter="16" className="cable_info">
            <Col span="12">
              <p>
                部件名称<span>{data?.Cable?.PartNumber || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                线长<span>{data?.Cable?.Length || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                SN<span>{data?.Cable?.SerialNumber || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                标识符<span>{data?.Cable?.Identifier || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                通信技术<span>{data?.Cable?.Technology || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                修订版本<span>{data?.Cable?.Revision || ''}</span>
              </p>
            </Col>
          </Row1>*/}
        </div>
      </Drawer>
    </div>
  )
}

export default styled(EdgeDrawer)`
  .title {
    margin-top: 16px;
    padding: 12px 20px 12px 20px;
    height: 52px;
    line-height: 28px;
    background: #f8f8fa;
    color: #3c4449;
    font-size: 14px;
  }
  .k-btns {
    .k-btn {
      &.level_1 {
        color: red;
      }
      &.k-active {
        color: #0091ea;
      }
    }
  }
  .k-table {
  }
  .cable_info {
    p {
      padding: 8px 0;
      margin: 0;
      color: #848f9a;
      span {
        margin-left: 8px;
        color: #3c4449;
      }
    }
  }
`
